/** Binary exception to apiFetch: JSON/text buffering cannot stream a recording.
 * No UI is activated. Each owner disposes this handle at selection/unmount and
 * installs the native-video retirement callback before using its cookie URL.
 */
import { resourceMediaRequest } from './resourceMediaRequest';
import type { ResourceMediaSelection, ResourceReadScope, ResourceFilmstripWindow } from './types';
import { ApiError, captureFeedbackConversationBoundary, feedbackConversationAuthorityFingerprint,
  getAuthTokenForRequest, getDevClubOverride, observeAuthTokenChanges } from './api';
import { useAuthStore } from '@/stores/auth';
import { useScopeStore } from '@/stores/scope';
import { queryClient, scopedKeyOf } from './queryClient';

export class ResourceMediaResponseError extends ApiError {
  constructor(status: number, detail: unknown, readonly retryAfter: string | null) { super(status, detail); }
}
const MAX_THUMBNAIL = 1024 * 1024;

export function createResourceMediaHandle(scope: ResourceReadScope, selection: ResourceMediaSelection) {
  const captured = { ...scope };
  const target = { ...selection };
  const boundary = captureFeedbackConversationBoundary({ ...captured, view: 'view' in target ? target.view : 'coach' });
  const intent = { team_id: captured.teamId, season: captured.seasonId === null
    ? { mode: 'unseasoned' as const } : { mode: 'current' as const, season_id: captured.seasonId } };
  const descriptor = resourceMediaRequest(intent, target, 'media');
  const authority = feedbackConversationAuthorityFingerprint();
  const view = 'view' in target ? target.view : 'coach';
  const key = scopedKeyOf(captured, 'resource-media', { actorId: captured.actorId, view, authority,
    resourceId: ('resourceId' in target ? target.resourceId : target.recordingId).toLowerCase(), path: descriptor.path, query: descriptor.query });
  const controller = new AbortController();
  const urls = new Set<string>();
  const retireCallbacks = new Set<() => void>();
  const bodyClosers = new Set<() => Promise<void>>();
  let disposed = false;
  const subscriptions: (() => void)[] = [];
  function dispose() {
    if (disposed) return;
    disposed = true; controller.abort(); boundary.dispose();
    subscriptions.splice(0).forEach(unsubscribe => unsubscribe());
    for (const url of urls) URL.revokeObjectURL(url);
    urls.clear();
    for (const close of bodyClosers) void close().catch(() => {});
    bodyClosers.clear();
    for (const callback of retireCallbacks) {
      try { callback(); } catch { /* Finish retiring every owned player/URL. */ }
    }
    retireCallbacks.clear();
  }
  const current = () => !disposed && boundary.current();
  const assertCurrent = () => { if (!current()) { dispose(); throw new DOMException('Resource media context changed', 'AbortError'); } };
  const observe = () => { if (!current()) dispose(); };
  subscriptions.push(useAuthStore.subscribe(observe), useScopeStore.subscribe(observe), observeAuthTokenChanges(observe));
  observe();

  async function retireProtectedCache() {
    // No blobs are put in QueryCache. Retire existing selected source/parent
    // projections on a current denial, without touching another actor's keys.
    const domains = (target.grant === 'coach' || target.grant === 'recording') ? ['coach-resources', 'resource-media']
      : ['feedback-conversations', 'feedback-collections', 'resource-media'];
    const selected = new Set(queryClient.getQueryCache().findAll({ predicate: ({ queryKey: candidate }) => {
      const extra = candidate[4];
      return domains.includes(String(candidate[0])) && candidate[1] === captured.clubId
        && candidate[2] === captured.teamId && candidate[3] === captured.seasonId
        && typeof extra === 'object' && extra !== null && 'actorId' in extra && extra.actorId === captured.actorId
        && (candidate[0] === 'coach-resources' || ('view' in extra && extra.view === view
          && 'authority' in extra && extra.authority === authority))
        && (candidate[0] !== 'resource-media' || ('resourceId' in extra && extra.resourceId === ('resourceId' in target ? target.resourceId : target.recordingId).toLowerCase()))
        && (target.grant === 'coach' || target.grant === 'recording' || candidate[0] !== 'feedback-conversations'
          || ('feedbackId' in extra && extra.feedbackId === target.feedbackId.toLowerCase()
            && 'playerId' in extra && extra.playerId === target.playerId));
    } }));
    await queryClient.cancelQueries({ predicate: query => selected.has(query) }, { revert: false });
    queryClient.removeQueries({ predicate: query => selected.has(query) });
  }

  async function fetchBinary(kind: 'media' | 'thumbnail' | 'filmstrip', method: 'GET' | 'HEAD', range?: string, window?: ResourceFilmstripWindow) {
    assertCurrent();
    const request = resourceMediaRequest(intent, target, kind, method, range, window);
    const token = getAuthTokenForRequest(), club = getDevClubOverride();
    const response = await fetch(`${request.path}?${new URLSearchParams(request.query)}`, {
      method, credentials: 'include', cache: 'no-store', signal: controller.signal,
      headers: { ...request.headers, ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(club ? { 'X-Tktic-Club-Override': club } : {}) },
    }).catch(error => { assertCurrent(); throw error; });
    try { assertCurrent(); } catch (error) { await response.body?.cancel(); throw error; }
    if (!response.ok) {
      // HEAD intentionally has no JSON body; status alone remains useful.
      let detail: unknown = null;
      try { detail = method === 'HEAD' ? null : ((await response.json()) as { detail?: unknown }).detail; } catch { /* Closed status fallback. */ }
      assertCurrent();
      if ([401, 404, 409].includes(response.status)) {
        await retireProtectedCache(); assertCurrent(); dispose();
      }
      throw new ResourceMediaResponseError(response.status, detail, response.headers.get('Retry-After'));
    }
    return response;
  }

  function binaryBody(response: Response) {
    // One owner for cancellation before or during iteration. Cancelling the
    // Response stream directly after getReader() would reject because it locks.
    const reader = response.body?.getReader();
    let closed = false;
    async function close() {
      if (closed) return;
      closed = true;
      await reader?.cancel().catch(() => {});
      reader?.releaseLock();
      bodyClosers.delete(close);
    }
    bodyClosers.add(close);
    const body = (async function* () {
      if (!reader) return;
      try {
        while (!closed) {
          assertCurrent(); const next = await reader.read(); assertCurrent();
          if (next.done || closed) return;
          yield next.value;
        }
      } catch (error) { assertCurrent(); throw error; }
      finally { await close(); }
    })();
    return { body, close };
  }
  async function preview(kind: 'thumbnail' | 'filmstrip', window?: ResourceFilmstripWindow) {
    const response = await fetchBinary(kind, 'GET', undefined, window);
    try { assertCurrent(); } catch (error) { await response.body?.cancel(); throw error; }
    if (response.headers.get('content-type') !== 'image/jpeg') { await response.body?.cancel(); throw new Error(`invalid_resource_${kind}`); }
    const parts: Uint8Array<ArrayBuffer>[] = []; let length = 0;
    for await (const chunk of binaryBody(response).body) {
      length += chunk.byteLength;
      if (length > MAX_THUMBNAIL * (kind === 'filmstrip' ? 3 : 1)) throw new Error(`invalid_resource_${kind}`);
      parts.push(new Uint8Array(chunk));
    }
    assertCurrent();
    if (!length) throw new Error(`invalid_resource_${kind}`);
    const url = URL.createObjectURL(new Blob(parts, { type: 'image/jpeg' })); urls.add(url);
    return { url, dispose: () => { URL.revokeObjectURL(url); urls.delete(url); } };
  }
  return {
    key, current, dispose,
    onRetire(callback: () => void) {
      assertCurrent(); retireCallbacks.add(callback);
      return () => { retireCallbacks.delete(callback); };
    },
    async media(range?: string) {
      const response = await fetchBinary('media', 'GET', range);
      try { assertCurrent(); } catch (error) { await response.body?.cancel(); throw error; }
      return { status: response.status, headers: response.headers, ...binaryBody(response) };
    },
    async head(range?: string) {
      const response = await fetchBinary('media', 'HEAD', range);
      assertCurrent();
      return { status: response.status, headers: response.headers };
    },
    thumbnail: () => preview('thumbnail'),
    filmstrip: (window: ResourceFilmstripWindow) => preview('filmstrip', window),
    cookieMediaUrl(onRetire: () => void) {
      assertCurrent();
      if (getAuthTokenForRequest() || getDevClubOverride()) throw new Error('resource_media_header_transport_required');
      // Native media uses existing same-origin cookies. No credential is ever
      // placed in its URL; the callback clears/pause the owning video on loss.
      retireCallbacks.add(onRetire);
      return { url: `${descriptor.path}?${new URLSearchParams(descriptor.query)}`,
        dispose: () => { retireCallbacks.delete(onRetire); onRetire(); } };
    },
  };
}
